import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/redux/store';
import { TitleEntity, TitleSearchResponse } from '@/redux/slices/slice_types';
import { TitleChildrenResponseV2, TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { LoadingStatuses } from '@/constants/constants';
import axios from '@/_utils/axios';
import { sortTitleChildren } from '@/_utils/titleUtils';

export type TitleSidebarTabsValue = 'all' | 'favorites' | 'recent';
export type SelectedTitlesMap = Record<TitleEntity['id'] | TitleEntityV2['id'], { detail: TitleEntity | TitleEntityV2; children: TitleEntityV2[] }>;

export interface TitleNavigationDrawerState {
  isOpen: boolean;
  searchTerm: string;
  currentTab: TitleSidebarTabsValue;
  status: LoadingStatuses | null;
  statusById: Record<TitleEntity['id'] | TitleEntityV2['id'], LoadingStatuses>;
  titleSearchResults: TitleEntity[];
  recentTitles: TitleEntity[];
  selectedTitle: TitleEntity | null;
  selectedTitlesMap: SelectedTitlesMap;
  selectedSeasonFilter: TitleEntityV2 | null;
  selectedVersions: TitleEntityV2[];
}

const initialState: TitleNavigationDrawerState = {
  isOpen: false,
  searchTerm: '',
  currentTab: 'all',
  status: null,
  statusById: {},
  titleSearchResults: [],
  recentTitles: [],
  selectedTitle: null,
  selectedTitlesMap: {},
  selectedSeasonFilter: null,
  selectedVersions: []
};

export const getSeriesTitles = createAsyncThunk('titleNavigationDrawer/getTitleSeries', async (_, { rejectWithValue, getState }) => {
  const { titleNavigationDrawer } = getState() as RootState;
  const searchTerm = titleNavigationDrawer.searchTerm;
  try {
    const { data } = await axios.post<TitleSearchResponse>('inventory/titles/search/?page_size=25&page_num=0', {
      order_on: 'name',
      direction: 'asc',
      entity_types_filter: ['series'],
      search: searchTerm?.length ? searchTerm : undefined
    });
    return data.data;
  } catch (error: any) {
    console.error('Error fetching titles', error);
    return rejectWithValue(error.data);
  }
});

export const getTitleSeasons = createAsyncThunk('titleNavigationDrawer/getTitleSeasons', async (title: TitleEntity, { rejectWithValue }) => {
  try {
    const { data } = await axios.post<TitleChildrenResponseV2>(
      `/inventory/titles/${title.id}/children/?page_size=999`,
      {
        order_on: 'sequence_order',
        direction: 'asc',
        thumbnails: true,
        entity_types_filter: ['season']
      },
      { baseURL: '/api/v2/' }
    );
    const seasons = data.data
      .filter((title) => title.entity_type === 'season')
      .sort((a, b) => {
        if (a.sequence_order === b.sequence_order) {
          return a.name < b.name ? -1 : 1;
        }
        return 0;
      });
    return { detail: title, children: seasons };
  } catch (error: any) {
    console.error('Error fetching title seasons', error);
    return rejectWithValue(error.data);
  }
});

export const getTitleEpisodes = createAsyncThunk(
  'titleNavigationDrawer/getTitleEpisodes',
  async (title: TitleEntityV2, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await axios.post<TitleChildrenResponseV2>(
        `/inventory/titles/${title.id}/children/?page_size=999`,
        {
          order_on: 'sequence_order',
          direction: 'asc',
          thumbnails: true,
          entity_types_filter: ['episode']
        },
        { baseURL: '/api/v2/' }
      );
      const episodes = data.data.filter((title) => title.entity_type === 'episode').sort((a, b) => a.sequence_order - b.sequence_order);
      dispatch(getEpisodesVersions(episodes));
      return { title, episodes };
    } catch (error: any) {
      console.error('Error fetching title episodes', error);
      return rejectWithValue(error.data);
    }
  }
);

export const getEpisodesVersions = createAsyncThunk(
  'titleNavigationDrawer/getEpisodesVersions',
  async (episodes: TitleEntityV2[], { rejectWithValue }) => {
    const fetchVersionsPromises = episodes.map((ep) =>
      axios.post<TitleChildrenResponseV2>(
        `/inventory/titles/${ep.id}/children/?page_size=999`,
        {
          order_on: 'sequence_order',
          direction: 'asc',
          thumbnails: true,
          entity_types_filter: ['version']
        },
        { baseURL: '/api/v2/' }
      )
    );

    try {
      const response = await Promise.all(fetchVersionsPromises);
      return response.reduce(
        (acc, curr) => {
          const episodeId = curr.data.data?.[0].relationships.parent?.id;
          const episode = episodes.find((ep) => ep.id === episodeId);
          if (!episode) return acc;
          return [
            ...acc,
            {
              title: episode,
              versions: curr.data.data.filter((title) => title.entity_type === 'version').sort((a, b) => a.name.localeCompare(b.name))
            }
          ];
        },
        [] as { title: TitleEntityV2; versions: TitleEntityV2[] }[]
      );
    } catch (error: any) {
      console.error('Error fetching title versions', error);
      return rejectWithValue(error.data);
    }
  }
);

export const getTitleVersions = createAsyncThunk('titleNavigationDrawer/getTitleVersions', async (title: TitleEntityV2, { rejectWithValue }) => {
  try {
    const { data } = await axios.post<TitleChildrenResponseV2>(
      `/inventory/titles/${title.id}/children/?page_size=999`,
      {
        order_on: 'sequence_order',
        direction: 'asc',
        thumbnails: true,
        entity_types_filter: ['version']
      },
      { baseURL: '/api/v2/' }
    );
    const versions = data.data.filter((title) => title.entity_type === 'version').sort((a, b) => a.name.localeCompare(b.name));
    return { title, versions };
  } catch (error: any) {
    console.error('Error fetching title versions', error);
    return rejectWithValue(error.data);
  }
});

const titleNavigationDrawerSlice = createSlice({
  name: 'titleNavigationDrawer',
  initialState,
  reducers: {
    toggleDrawer: (state, { payload }: PayloadAction<TitleNavigationDrawerState['isOpen'] | undefined>) => {
      state.isOpen = payload !== undefined ? payload : !state.isOpen;
    },
    openViewAll: (state) => {
      state.currentTab = 'all';
      state.isOpen = true;
    },
    openRecent: (state) => {
      state.currentTab = 'recent';
      state.isOpen = true;
    },
    updateSearchTerm: (state, { payload }: PayloadAction<TitleNavigationDrawerState['searchTerm']>) => {
      state.searchTerm = payload;
    },
    resetSearch: (state) => {
      state.searchTerm = '';
      state.titleSearchResults = initialState.titleSearchResults;
    },
    updateCurrentTab: (state, { payload }: PayloadAction<TitleNavigationDrawerState['currentTab']>) => {
      state.currentTab = payload;
    },
    updateTitleSearchResults: (state, { payload }: PayloadAction<TitleNavigationDrawerState['titleSearchResults']>) => {
      state.titleSearchResults = payload;
    },
    selectTitle: (state, { payload }: PayloadAction<TitleEntity>) => {
      const sortedTitle = sortTitleChildren(payload);
      state.selectedTitle = sortedTitle;

      if (state.recentTitles.length === 0) {
        state.recentTitles = [sortedTitle];
      } else {
        const updatedRecentTitles = state.recentTitles.filter((title) => title.id !== sortedTitle.id);
        updatedRecentTitles.unshift(sortedTitle);
        state.recentTitles = updatedRecentTitles;
      }
    },
    resetSelectedTitle: (state) => {
      state.selectedTitle = null;
    },
    updateSelectedSeasonFilter: (state, { payload }: PayloadAction<TitleNavigationDrawerState['selectedSeasonFilter']>) => {
      state.selectedSeasonFilter = payload;
    },
    updateSelectedVersions: (state, { payload }: PayloadAction<TitleEntityV2 | undefined>) => {
      if (payload === undefined) {
        state.selectedVersions = [];
        return;
      }

      if (!state.selectedVersions.length) {
        state.selectedVersions = [payload];
        return;
      }

      if (state.selectedVersions.find((version) => version.id === payload.id)) {
        state.selectedVersions = state.selectedVersions.filter((version) => version.id !== payload.id);
        return;
      }

      state.selectedVersions = [...state.selectedVersions, payload];
    },
    replaceSelectedVersions: (state, { payload }: PayloadAction<TitleEntityV2[]>) => {
      state.selectedVersions = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getSeriesTitles.pending, (state) => {
      state.status = LoadingStatuses.pending;
    });
    builder.addCase(getSeriesTitles.rejected, (state) => {
      state.status = LoadingStatuses.rejected;
      state.titleSearchResults = initialState.titleSearchResults;
    });
    builder.addCase(getSeriesTitles.fulfilled, (state, { payload }) => {
      state.status = LoadingStatuses.fulfilled;
      state.titleSearchResults = payload;
    });

    builder.addCase(getTitleSeasons.pending, (state, action) => {
      state.statusById = { ...state.statusById, [action.meta.arg.id]: LoadingStatuses.pending };
    });
    builder.addCase(getTitleSeasons.rejected, (state, action) => {
      state.selectedTitle = initialState.selectedTitle;
      state.statusById = { ...state.statusById, [action.meta.arg.id]: LoadingStatuses.rejected };
    });
    builder.addCase(getTitleSeasons.fulfilled, (state, { payload }) => {
      const { detail, children } = payload;
      state.statusById = { ...state.statusById, [detail.id]: LoadingStatuses.fulfilled };
      state.selectedTitlesMap = { ...state.selectedTitlesMap, [detail.id]: { detail, children } };
      state.selectedSeasonFilter = children.length > 0 ? children[0] : null;
    });

    builder.addCase(getTitleEpisodes.pending, (state, action) => {
      state.statusById = { ...state.statusById, [action.meta.arg.id]: LoadingStatuses.pending };
    });
    builder.addCase(getTitleEpisodes.rejected, (state, action) => {
      state.selectedSeasonFilter = initialState.selectedSeasonFilter;
      state.statusById = { ...state.statusById, [action.meta.arg.id]: LoadingStatuses.rejected };
    });
    builder.addCase(getTitleEpisodes.fulfilled, (state, { payload }) => {
      const { title, episodes } = payload;
      state.statusById = { ...state.statusById, [title.id]: LoadingStatuses.fulfilled };
      state.selectedTitlesMap = { ...state.selectedTitlesMap, [title.id]: { detail: title, children: episodes } };
    });

    builder.addCase(getEpisodesVersions.pending, (state, action) => {
      action.meta.arg.forEach((episode) => {
        state.statusById = { ...state.statusById, [episode.id]: LoadingStatuses.pending };
      });
    });
    builder.addCase(getEpisodesVersions.rejected, (state, action) => {
      action.meta.arg.forEach((episode) => {
        state.statusById = { ...state.statusById, [episode.id]: LoadingStatuses.rejected };
      });
    });
    builder.addCase(getEpisodesVersions.fulfilled, (state, { payload }) => {
      payload.forEach(({ title, versions }) => {
        state.statusById = { ...state.statusById, [title.id]: LoadingStatuses.fulfilled };
        state.selectedTitlesMap = {
          ...state.selectedTitlesMap,
          [title.id]: { detail: title, children: versions }
        };
      });
    });

    builder.addCase(getTitleVersions.pending, (state, action) => {
      state.statusById = { ...state.statusById, [action.meta.arg.id]: LoadingStatuses.pending };
    });
    builder.addCase(getTitleVersions.rejected, (state, action) => {
      state.statusById = { ...state.statusById, [action.meta.arg.id]: LoadingStatuses.rejected };
      state.selectedTitle = initialState.selectedTitle;
    });
    builder.addCase(getTitleVersions.fulfilled, (state, { payload }) => {
      const { title, versions } = payload;
      state.statusById = { ...state.statusById, [title.id]: LoadingStatuses.fulfilled };
      state.selectedTitlesMap = { ...state.selectedTitlesMap, [title.id]: { detail: title, children: versions } };
    });
  }
});

export const {
  toggleDrawer,
  openViewAll,
  openRecent,
  updateSearchTerm,
  resetSearch,
  updateCurrentTab,
  updateTitleSearchResults,
  selectTitle,
  resetSelectedTitle,
  updateSelectedSeasonFilter,
  updateSelectedVersions,
  replaceSelectedVersions
} = titleNavigationDrawerSlice.actions;

export default titleNavigationDrawerSlice.reducer;
